import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { contentApi } from '../../services/api';
import { useAuthComplete } from '../../contexts/AuthContext';
import { getErrorMessage, slugify } from '../../utils/helpers';
import RichTextEditor from '../common/RichTextEditor';

function ContentForm() {
  const navigate = useNavigate();
  const { contentId } = useParams();
  const isEditing = Boolean(contentId);

  const { isAuthenticated, user } = useAuthComplete();

  const [formData, setFormData] = useState({
    title: '',
    body: '',
    excerpt: '',
    type: 'post',
    tags: [],
    slug: '',
    isPublished: false
  });

  const [tagInput, setTagInput] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingContent, setLoadingContent] = useState(isEditing);
  const [error, setError] = useState(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  // Load existing content for editing
  const fetchExistingContent = useCallback(async () => {
    setLoadingContent(true);
    try {
      const response = await contentApi.getContentById(contentId);
      const content = response.content;

      // Check if user can edit this content
      const canEdit = user && (user.id === content.authorId || user.roles.includes('admin'));
      if (!canEdit) {
        navigate('/content');
        return;
      }

      setFormData({
        title: content.title || '',
        body: content.body || '',
        excerpt: content.excerpt || '',
        type: content.type || 'post',
        tags: content.tags || [],
        slug: content.slug || '',
        isPublished: content.isPublished || false
      });
    } catch (err) {
      console.error('Failed to fetch content for editing:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoadingContent(false);
    }
  }, [contentId, navigate, user]);

  useEffect(() => {
    if (isEditing && contentId) {
      fetchExistingContent();
    }
  }, [isEditing, contentId, fetchExistingContent]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({ ...prev, [name]: newValue }));

    // Auto-generate slug from title if not editing or if slug is empty
    if (name === 'title' && (!isEditing || !formData.slug)) {
      const autoSlug = slugify(value);
      setFormData(prev => ({ ...prev, slug: autoSlug }));
    }

    // Clear field errors when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }

    // Clear general error
    if (error) {
      setError(null);
    }
  };

  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag) && formData.tags.length < 10) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.length > 200) {
      errors.title = 'Title must be less than 200 characters';
    }

    if (!formData.body.trim()) {
      errors.body = 'Content body is required';
    } else if (formData.body.length > 50000) {
      errors.body = 'Content body is too long (max 50,000 characters)';
    }

    if (!formData.slug.trim()) {
      errors.slug = 'Slug is required';
    }

    if (formData.excerpt.length > 500) {
      errors.excerpt = 'Excerpt must be less than 500 characters';
    }

    if (formData.tags.length > 10) {
      errors.tags = 'Maximum 10 tags allowed';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const contentData = {
        title: formData.title.trim(),
        body: formData.body.trim(),
        excerpt: formData.excerpt.trim(),
        type: formData.type,
        tags: formData.tags,
        slug: formData.slug.trim(),
        isPublished: formData.isPublished
      };

      let result;
      if (isEditing) {
        result = await contentApi.updateContent(contentId, contentData);
      } else {
        result = await contentApi.createContent(contentData);
      }

      const createdContent = result.content;
      navigate(`/content/${createdContent.slug || createdContent.id}`);
    } catch (err) {
      console.error('Failed to save content:', err);
      
      // Handle validation errors from server
      if (err.errors) {
        setFieldErrors(err.errors);
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingContent) {
    return (
      <div className="content-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-form-container">
      <div className="content-form-header">
        <h1>{isEditing ? 'Edit Content' : 'Create New Content'}</h1>
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="content-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={fieldErrors.title ? 'error' : ''}
              placeholder="Enter content title"
              maxLength="200"
              required
            />
            {fieldErrors.title && (
              <span className="field-error">{fieldErrors.title}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="type">Type</label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="post">Post</option>
              <option value="page">Page</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="slug">URL Slug *</label>
          <input
            type="text"
            id="slug"
            name="slug"
            value={formData.slug}
            onChange={handleInputChange}
            className={fieldErrors.slug ? 'error' : ''}
            placeholder="url-friendly-slug"
            maxLength="100"
            required
          />
          {fieldErrors.slug && (
            <span className="field-error">{fieldErrors.slug}</span>
          )}
          <small className="form-help">
            Used in the URL. Auto-generated from title if left empty.
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="excerpt">Excerpt</label>
          <textarea
            id="excerpt"
            name="excerpt"
            value={formData.excerpt}
            onChange={handleInputChange}
            className={fieldErrors.excerpt ? 'error' : ''}
            placeholder="Brief description (optional)"
            rows="3"
            maxLength="500"
          />
          {fieldErrors.excerpt && (
            <span className="field-error">{fieldErrors.excerpt}</span>
          )}
          <small className="char-count">
            {formData.excerpt.length}/500 characters
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="body">Content *</label>
          <RichTextEditor
            id="body"
            value={formData.body}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, body: value }));
              if (fieldErrors.body) {
                setFieldErrors(prev => ({ ...prev, body: null }));
              }
              if (error) {
                setError(null);
              }
            }}
            placeholder="Write your content here..."
            maxLength={50000}
            error={fieldErrors.body}
          />
        </div>

        <div className="form-group">
          <label htmlFor="tags">Tags</label>
          <div className="tags-input-container">
            <input
              type="text"
              id="tags"
              value={tagInput}
              onChange={handleTagInputChange}
              onKeyDown={handleTagInputKeyDown}
              placeholder="Add tags (press Enter or comma to add)"
              className="tags-input"
            />
            <button type="button" onClick={addTag} className="btn btn-sm btn-secondary">
              Add
            </button>
          </div>
          
          {formData.tags.length > 0 && (
            <div className="tags-display">
              {formData.tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="tag-remove"
                    aria-label={`Remove ${tag} tag`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          
          {fieldErrors.tags && (
            <span className="field-error">{fieldErrors.tags}</span>
          )}
          <small className="form-help">
            {formData.tags.length}/10 tags
          </small>
        </div>

        <div className="form-group">
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="isPublished"
              name="isPublished"
              checked={formData.isPublished}
              onChange={handleInputChange}
            />
            <label htmlFor="isPublished">
              Publish immediately
            </label>
          </div>
          <small className="form-help">
            Unpublished content is only visible to you and admins.
          </small>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/content')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Content' : 'Create Content')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ContentForm;